/**
 * The dev-only debug overlay: frame timing, entity counts and ping, refreshed
 * into uiState every couple of seconds. The hitch log (HitchLog.ts) takes its
 * frame hooks from here, so the loop calls two functions a frame either way.
 */
import type { DebugStats } from '../ui/UIState';
import { uiState } from '../ui/UIState';
import { clock } from './Clock';
import { DEVELOPMENT } from './Constants';
import { count as doodadsCount } from './Doodads';
import { viewport } from './Graphics';
import { frameEnd as hitchFrameEnd, frameStart as hitchFrameStart, heapMb } from './HitchLog';
import { count as mobsCount } from './Mobs';
import { timing as networkTiming } from './Network';
import { count as particlesCount } from './particles/Particles';
import { count as playersCount, getMe as playersGetMe } from './Players';

/** Legacy's refresh period, offset from the one-second timers around it. */
const REFRESH_INTERVAL_MS = 2123;

/** Whether the overlay is up. Raised by setup and lowered by hide; the frame hooks do nothing while it is down. */
let active = false;

/** The refresh timer, cleared by hide. */
let refreshTimer: ReturnType<typeof setInterval> | null = null;

/** Whether the tab is hidden, kept by a visibility listener while the overlay is up so the frame hook reads no DOM. */
let tabHidden = false;

function onVisibilityChange(): void {
  tabHidden = document.hidden;
}

/** The counters behind a snapshot. */
interface Counters {
  /** When the last snapshot was taken. */
  last: number;
  /** Time spent inside the frame loop since then. */
  ticks: number;
  /** clock.frames at the last snapshot. */
  frames: number;
  /** The start of the frame being timed for ticks, null between frames. */
  startedFrame: number | null;
  /** The start of the previous frame, for the frame time; null while the tab is hidden. */
  previousFrame: number | null;
  /** The shortest and longest frame since the last snapshot. */
  frameTimeMin: number;
  frameTimeMax: number;
}

const counters: Counters = {
  last: 0,
  ticks: 0,
  frames: 0,
  startedFrame: null,
  previousFrame: null,
  frameTimeMin: Infinity,
  frameTimeMax: 0,
};

/** Starts a new measuring period at `now`. */
function resetCounters(now: number): void {
  counters.last = now;
  counters.ticks = 0;
  counters.frames = clock.frames;
  counters.frameTimeMin = Infinity;
  counters.frameTimeMax = 0;
}

/**
 * Shows the overlay and starts its refresh (dev mode only). Called once the
 * systems it counts are set up, so the first snapshot is real; legacy set it
 * up first, when only its div existed to show.
 */
export function setup(): void {
  if (!DEVELOPMENT) {
    return;
  }

  active = true;
  tabHidden = document.hidden;
  document.addEventListener('visibilitychange', onVisibilityChange);
  resetCounters(performance.now());
  // A non-null snapshot mounts the panel; the timing fills in at the first refresh
  uiState.debugStats = snapshot(0, 0, 0);
  refreshTimer = setInterval(refresh, REFRESH_INTERVAL_MS);
}

/** Hides the overlay and stops its refresh and its frame hooks. */
export function hide(): void {
  active = false;
  document.removeEventListener('visibilitychange', onVisibilityChange);
  if (refreshTimer !== null) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
  uiState.debugStats = null;
}

/** Publishes a snapshot of the period since the last one, then starts the next. */
function refresh(): void {
  const now = performance.now();
  const elapsed = now - counters.last;
  const frames = clock.frames - counters.frames;
  const fps = (1000 * frames) / elapsed;
  const tickPercentage = (100 * counters.ticks) / elapsed;
  const frameTime = frames > 0 ? elapsed / frames : 0;
  uiState.debugStats = snapshot(fps, tickPercentage, frameTime);
  resetCounters(now);
}

/** The stats as of now: the timing given, the counts and the position read live. The panel formats them. */
function snapshot(fps: number, tickPercentage: number, frameTime: number): DebugStats {
  const [playersActive, playersTotal] = playersCount();
  const [mobsActive, mobsTotal] = mobsCount();
  const [doodadsVisible, doodadsTotal] = doodadsCount();
  const me = playersGetMe();
  const timed = counters.frameTimeMin !== Infinity;
  return {
    fps,
    tickPercentage,
    ping: networkTiming.ping,
    screenWidth: viewport.width,
    screenHeight: viewport.height,
    renderResolution: viewport.resolution,
    playersActive,
    playersTotal,
    mobsActive,
    mobsTotal,
    particles: particlesCount(),
    doodadsVisible,
    doodadsTotal,
    coordinates: me === undefined ? null : { x: Math.round(me.pos.x), y: Math.round(me.pos.y) },
    scale: viewport.scale,
    jitter: networkTiming.jitter,
    frameTime,
    frameTimeMin: timed ? counters.frameTimeMin : null,
    frameTimeMax: timed ? counters.frameTimeMax : null,
    memory: heapMb(),
  };
}

/**
 * Marks the frame's start: the hitch log first, then, with the overlay up,
 * the frame is timed for ticks, and the gap since the previous frame goes
 * into the min and max. The keep-alive frames of a hidden tab come half a
 * second apart and are not frame times, so they are left out.
 */
export function startFrame(): void {
  hitchFrameStart();
  if (!active) {
    return;
  }

  const now = performance.now();
  counters.startedFrame = now;

  if (tabHidden) {
    counters.previousFrame = null;
    return;
  }
  if (counters.previousFrame !== null) {
    const frameTime = now - counters.previousFrame;
    if (frameTime < counters.frameTimeMin) counters.frameTimeMin = frameTime;
    if (frameTime > counters.frameTimeMax) counters.frameTimeMax = frameTime;
  }
  counters.previousFrame = now;
}

/** Adds the frame's time to ticks, and ends the hitch log's frame. */
export function endFrame(): void {
  hitchFrameEnd();
  if (!active || counters.startedFrame === null) {
    return;
  }

  counters.ticks += performance.now() - counters.startedFrame;
  counters.startedFrame = null;
}
